import { Guest, Picture } from "@/type/core/offices/offices.types";
import {
  Consent,
  QcpCertificateCreation,
  UserCertificatePost,
} from "@/type/core/qcp/qcp.types";
import { SignaturePicturePriorityEnum } from "@/utils/enums/signature.picture.priority.enum";
import AddGuestPostBodyDto from "@state/users/dto/body/add.guest.post.body.dto";
import SearchGuestUpdateBodyDto from "@state/users/dto/body/search.guest.update.body.dto";
import SearchGuestsPostBodyDto from "@state/users/dto/body/search.guests.post.body.dto";
import UpdateGuestPicturePriorityBodyDto from "@state/users/dto/body/update.guest.picture.priority.body.dto";
import { UserRevokeCertificateBodyDTO } from "@state/users/dto/body/user.revoke.certificate.body.dto";
import AddGuestResponseDto from "@state/users/dto/response/add.guest.response.dto";
import SearchGuestsResponseDto from "@state/users/dto/response/search.guests.response.dto";
import UpdateGuestPictureResponseDto from "@state/users/dto/response/update.guest.picture.response.dto";
import UserMembersResponseDto from "@state/users/dto/response/user.members.response.dto";
import { EcUserDTO } from "@type/core/user/EcUserDTO";
import configuration from "@utils/Config";
import {
  restDeletionHandler,
  restGetHandler,
  restPostHandler,
  restUpdateHandler,
  restUpdateHandlerWithParams,
  restUpdateMultipartHandler,
} from "@utils/rest/RestClient";
import { createEffect } from "effector";
import UserPictureDeleteBodyDTO from "@state/users/dto/body/user.picture.delete.body.dto";
import UserPicturePriorityBody from "@state/users/dto/body/user.picture.priority.body.dto";

const baseUrl = configuration.backendBaseUrl;
const usersUrl = baseUrl + "/ws/fo/user";
const userSignaturePicturesUrl = usersUrl + "/signaturePictures";
const userSignaturePriorityUrl = usersUrl + "/signature/priority";
const userAgreementUrl = usersUrl + "/agreement";
const userRevokeCertificateUrl = usersUrl + "/certificate/revoke";
const membersUrl = usersUrl + "/members";
const guestUrl = baseUrl + "/ws/fo/guest";
const searchGuestUrl = guestUrl + "/search";
const guestSignature = "/signature";
const signaturePriorityUrl = "?signaturePriority=";
const allPriorityUrl = guestUrl + guestSignature + "/priority";
const priorityUrl = guestSignature + "/priority" + signaturePriorityUrl;
const userCertificateQcpRegistrationUrl =
  usersUrl + "/certificate/qcp/registration";
const userCertificateQcpRegistrationResumeUrl =
  userCertificateQcpRegistrationUrl + "/resume";
const userCguUrl = usersUrl + "/cgu";
const userResetCguUrl = usersUrl + "/cgu/reset";
const userConsentUrl = usersUrl + "/consent";

export const requestUpdateUserPicture = createEffect({
  handler: restUpdateMultipartHandler<void>(usersUrl),
});

export const requestDeleteUserPicture = createEffect({
  handler: restDeletionHandler<UserPictureDeleteBodyDTO>(usersUrl),
});

export const requestGetUserSignaturePictures = createEffect({
  handler: restGetHandler<Picture[]>(userSignaturePicturesUrl),
});

export const requestUpdateUserSignaturePriority = createEffect({
  handler: restUpdateHandler<UserPicturePriorityBody, void>(
    userSignaturePriorityUrl,
  ),
});

export const requestUserGetSignaturePriority = createEffect({
  handler: restGetHandler<SignaturePicturePriorityEnum>(
    userSignaturePriorityUrl,
  ),
});

export const requestGetUserAgreement = createEffect({
  handler: restGetHandler<File>(
    userAgreementUrl,
    undefined,
    "application/json",
    "application/pdf",
  ),
});

export const requestPostUserRevokeCertificate = createEffect({
  handler: restPostHandler<UserRevokeCertificateBodyDTO, EcUserDTO>(
    userRevokeCertificateUrl,
  ),
});

export const requestPostMembersForOffice = createEffect({
  handler: restPostHandler<string[], UserMembersResponseDto>(membersUrl),
});

export const requestSearchGuestsForOffice = createEffect({
  handler: restPostHandler<SearchGuestsPostBodyDto, SearchGuestsResponseDto>(
    searchGuestUrl,
  ),
});

export const requestAddGuestForOffices = createEffect({
  handler: restPostHandler<AddGuestPostBodyDto, AddGuestResponseDto>(guestUrl),
});

export const requestUpdateGuestForOffice = createEffect({
  handler: restUpdateHandler<SearchGuestUpdateBodyDto, Guest[]>(guestUrl),
});

export const requestDeleteGuestForOffices = createEffect({
  handler: restDeletionHandler<void>(guestUrl),
});

export const requestUpdateGuestPicture = createEffect({
  handler: restUpdateMultipartHandler<UpdateGuestPictureResponseDto>(
    guestUrl,
    guestSignature,
  ),
});

export const requestDeleteGuestPicture = createEffect({
  handler: restDeletionHandler<void>(guestUrl, guestSignature),
});

export const requestUpdateGuestPicturePriority = createEffect({
  handler: restUpdateHandlerWithParams<
    UpdateGuestPicturePriorityBodyDto,
    Guest[]
  >(guestUrl, priorityUrl),
});

export const requestUpdateAllGuestPicturePriority = createEffect({
  handler: restUpdateHandlerWithParams<
    UpdateGuestPicturePriorityBodyDto,
    Guest[]
  >(allPriorityUrl, signaturePriorityUrl),
});

export const requestCreateUserCertificateQcpRegistration = createEffect({
  handler: restPostHandler<UserCertificatePost, QcpCertificateCreation>(
    userCertificateQcpRegistrationUrl,
  ),
});

export const requestCreateUserCertificateQcpRegistrationResume = createEffect({
  handler: restPostHandler<UserCertificatePost, QcpCertificateCreation>(
    userCertificateQcpRegistrationResumeUrl,
  ),
});

export const requestUpdateUserCgu = createEffect({
  handler: restPostHandler<void, void>(userCguUrl),
});

export const requestResetUserCgu = createEffect({
  handler: restPostHandler<void, void>(userResetCguUrl),
});

export const postUserConsent = createEffect({
  handler: restPostHandler<Consent, void>(userConsentUrl),
});

export const getUserConsent = createEffect({
  handler: restGetHandler<Consent>(userConsentUrl),
});
